import i18n from "i18next";
import type { PostProcessorModule } from "i18next";
import { initReactI18next } from "react-i18next";

import { getWorkspace, subscribe as onWorkspaceChange } from "@/api/workspace";

import ar from "./locales/ar.json";
import en from "./locales/en.json";

export const LANGUAGES = [
    { code: "en", label: "English", dir: "ltr" },
    { code: "ar", label: "العربية", dir: "rtl" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

const STORAGE_KEY = "pm.lang";

/** The server names a language `ar_001`; the bundles are keyed `ar`. */
export function uiLanguage(serverCode: string | null | undefined): LanguageCode | null {
    if (!serverCode) {
        return null;
    }
    const short = serverCode.split(/[-_]/)[0];
    return LANGUAGES.some((item) => item.code === short) ? (short as LanguageCode) : null;
}

export function storedLanguage(): LanguageCode | null {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        return LANGUAGES.some((item) => item.code === value)
            ? (value as LanguageCode)
            : null;
    } catch {
        return null;
    }
}

export function rememberLanguage(code: LanguageCode) {
    try {
        localStorage.setItem(STORAGE_KEY, code);
    } catch {
        /* a private window may refuse storage; the choice simply is not kept */
    }
}

export function applyDirection(code: string) {
    const entry = LANGUAGES.find((item) => item.code === code) ?? LANGUAGES[0];
    document.documentElement.lang = entry.code;
    document.documentElement.dir = entry.dir;
    document.body.classList.toggle("o_rtl", entry.dir === "rtl");
}

/** Follow the language the account carries, which is what the API answers in. */
export async function followServerLanguage(serverCode: string | null | undefined) {
    const code = uiLanguage(serverCode);
    if (code && code !== i18n.language) {
        await i18n.changeLanguage(code);
    }
}

/** Where consulting services keep the words of a text a design names after itself. */
const CONSULTING_TERMS = "consultingTerms";

/** In the consulting services workspace, a text that speaks of design is read
 *  from `consultingTerms` under the same key, when that names it otherwise. */
const consultingWords: PostProcessorModule = {
    type: "postProcessor",
    name: "consultingWords",
    process(value, key, options) {
        const name = Array.isArray(key) ? key[0] : key;
        if (getWorkspace() !== "consulting" || !name || name.startsWith(CONSULTING_TERMS)) {
            return value;
        }
        const alias = `${CONSULTING_TERMS}.${name}`;
        return i18n.exists(alias) ? i18n.t(alias, { ...options, postProcess: [] }) : value;
    },
};

void i18n.use(initReactI18next).use(consultingWords).init({
    resources: { en: { translation: en }, ar: { translation: ar } },
    lng: storedLanguage() ?? "en",
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    returnNull: false,
    postProcess: ["consultingWords"],
});

// the words follow the workspace as they follow the language
onWorkspaceChange(() => {
    i18n.emit("languageChanged", i18n.language);
});

applyDirection(i18n.language);

i18n.on("languageChanged", (code) => {
    applyDirection(code);
    rememberLanguage(code as LanguageCode);
});

export default i18n;
